/**
 * GameScreen.tsx
 * Game screen for real matches (via matchmaking)
 */

import {
  type FC,
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';

import {
  positionToSquare,
  type IChessStateFen,
  type Color,
  type TPlayerSlot,
} from '@skillarea/game-chess';
import {
  checkAllSunk,
  type IBattleshipGameState,
} from '@skillarea/game-battleship';
import {
  checkWinner as checkWinnerFour,
  checkDraw as checkDrawFour,
} from '@skillarea/game-fourinarow';
import type { TBoard as TFourInARowBoard01 } from '@skillarea/game-fourinarow';
import {
  checkWinner as checkWinnerTtt,
  checkDraw as checkDrawTtt,
} from '@skillarea/game-tictactoe';
import type { TBoard as TTicTacToeBoard01 } from '@skillarea/game-tictactoe';
import type { TPlayer } from '@skillarea/game-tictactoe-phaser';
import { Spinner, TextBlock } from '@skillarea/lib-frontend';
import { useTranslation } from 'react-i18next';

import styles from '../MatchmakingModal.module.scss';
import { GameScreenRoot } from '../components/GameScreenRoot';
import { useTurnStamp } from '../hooks/useTurnStamp';
import { getActiveEngineSlotFromState } from '../utils/engine-slot';

import { BattleshipGame } from '~/components/games/BattleshipGame';
import { ChessGame } from '~/components/games/ChessGame';
import { FourInARowGame } from '~/components/games/FourInARowGame';
import { TicTacToeGame } from '~/components/games/TicTacToeGame';
import type { TGamePlayer } from '~/components/games/types';

interface ITicTacToeBackendState {
  board: (null | 0 | 1)[][];
  currentPlayer: 0 | 1;
  moves: number;
}

interface IFourInARowBackendState {
  board: (null | 0 | 1)[][];
  currentPlayer: 0 | 1;
}

function convertBackendBoardTicTacToe(
  backendBoard: (null | 0 | 1)[][],
): (TPlayer | null)[][] {
  return backendBoard.map((row) =>
    row.map((cell) => (cell === 0 ? 0 : cell === 1 ? 1 : null)),
  );
}

function isBackendBoardState(
  state: unknown,
): state is { board: (null | 0 | 1)[][]; currentPlayer: 0 | 1 } {
  if (typeof state !== 'object' || state === null) return false;
  const record = state as Record<string, unknown>;
  if (!Array.isArray(record.board)) return false;
  return record.currentPlayer === 0 || record.currentPlayer === 1;
}

function isBattleshipBackendState(
  state: unknown,
): state is IBattleshipGameState {
  if (typeof state !== 'object' || state === null) return false;
  const record = state as Record<string, unknown>;
  return (
    'ships0' in record &&
    'ships1' in record &&
    'shotsAt0' in record &&
    'shotsAt1' in record &&
    (record.currentPlayer === 0 || record.currentPlayer === 1)
  );
}

function resolveTurnDurationMsFromEngineTimes(
  engineSlot0Ms: number | undefined,
  engineSlot1Ms: number | undefined,
): number | undefined {
  if (
    typeof engineSlot0Ms !== 'number' ||
    typeof engineSlot1Ms !== 'number' ||
    !Number.isFinite(engineSlot0Ms) ||
    !Number.isFinite(engineSlot1Ms)
  ) {
    return undefined;
  }

  return Math.max(engineSlot0Ms, engineSlot1Ms);
}

function mapEngineSlotTimesToLayout(
  engineSlot0Ms: number,
  engineSlot1Ms: number,
  myEngineSlot: 0 | 1,
): { layoutP1: number; layoutP2: number } {
  return myEngineSlot === 0
    ? { layoutP1: engineSlot0Ms, layoutP2: engineSlot1Ms }
    : { layoutP1: engineSlot1Ms, layoutP2: engineSlot0Ms };
}

function applyLiveEngineTimes(
  server0: number,
  server1: number,
  activeEngineSlot: 0 | 1,
  elapsedMs: number,
): { live0: number; live1: number } {
  const live0 =
    activeEngineSlot === 0 ? Math.max(0, server0 - elapsedMs) : server0;
  const live1 =
    activeEngineSlot === 1 ? Math.max(0, server1 - elapsedMs) : server1;
  return { live0, live1 };
}

interface IGameUpdateData {
  lobbyId: string;
  gameState: unknown;
  yourTurn: boolean;
  /** Player slot (0 or 1), set by the game engine. */
  playerSlot?: 0 | 1;
  /** Remaining time for engine slot 0. */
  player1TimeLeftMs?: number;
  /** Remaining time for engine slot 1. */
  player2TimeLeftMs?: number;
  message?: string;
}

interface IGameScreenProps {
  gameSlug: string;
  lobbyId?: string;
  player1: TGamePlayer;
  player2: TGamePlayer;
  gameUpdate: IGameUpdateData | null;
  /** Slots 0/1 from game_started until a game_update with timers arrives. */
  initialPlayer1TimeLeftMs?: number;
  initialPlayer2TimeLeftMs?: number;
  /** My slot from game_started (before the first game_update). */
  initialPlayerSlot?: 0 | 1;
  rules: string;
  turnDurationMs?: number;
  onMove: (action: unknown) => void;
  onGameEnd?: (result: unknown) => void;
  onSurrender?: () => void;
  /** Called when a timer reaches 0 (so the server can check timeout and send game_finished). */
  onTimeUp?: () => void;
}

export const GameScreen: FC<IGameScreenProps> = ({
  gameSlug,
  gameUpdate,
  initialPlayer1TimeLeftMs,
  initialPlayer2TimeLeftMs,
  initialPlayerSlot,
  lobbyId,
  onGameEnd,
  onMove,
  onSurrender,
  onTimeUp,
  player1: player1Info,
  player2: player2Info,
  rules,
  turnDurationMs: turnDurationMsProp,
}) => {
  const playerSymbolRef = useRef<TPlayer | null>(null);
  const battleshipPlayerSlotRef = useRef<0 | 1 | null>(null);
  const chessPlayerSlotRef = useRef<0 | 1 | null>(null);
  const lastUpdateTimeRef = useRef<number>(Date.now());
  const serverEngine0Ref = useRef(0);
  const serverEngine1Ref = useRef(0);
  const hasEngineSnapshotRef = useRef(false);
  const isMyTurnRef = useRef(false);
  const timeUpRequestedRef = useRef(false);
  const initializedLobbyIdRef = useRef<string | null>(null);
  const gameUpdateRef = useRef<IGameUpdateData | null>(null);
  const gameSlugRef = useRef(gameSlug);
  const onTimeUpRef = useRef(onTimeUp);
  const lastGameStateRef = useRef<unknown>(undefined);
  const lastMySlotRef = useRef<0 | 1 | null>(null);
  const initialPlayerSlotRef = useRef(initialPlayerSlot);
  initialPlayerSlotRef.current = initialPlayerSlot;

  gameUpdateRef.current = gameUpdate;
  gameSlugRef.current = gameSlug;
  onTimeUpRef.current = onTimeUp;
  isMyTurnRef.current = gameUpdate?.yourTurn ?? false;

  const [localPlayer1Ms, setLocalPlayer1Ms] = useState(0);
  const [localPlayer2Ms, setLocalPlayer2Ms] = useState(0);
  const isMyTurn = gameUpdate?.yourTurn ?? false;
  const activeLobbyId = gameUpdate?.lobbyId ?? lobbyId ?? null;

  const turnStamp = useTurnStamp({
    activeLobbyId,
    gameSlug,
    gameUpdate,
    initialPlayerSlot,
    isMyTurn,
  });

  const player1 = useMemo(
    () => ({ name: player1Info.name, avatarUrl: player1Info.avatarUrl }),
    [player1Info.avatarUrl, player1Info.name],
  );
  const player2 = useMemo(
    () => ({ name: player2Info.name, avatarUrl: player2Info.avatarUrl }),
    [player2Info.avatarUrl, player2Info.name],
  );

  const turnDurationMs = useMemo(() => {
    const fromUpdate = resolveTurnDurationMsFromEngineTimes(
      gameUpdate?.player1TimeLeftMs,
      gameUpdate?.player2TimeLeftMs,
    );
    if (fromUpdate != null) {
      return fromUpdate;
    }

    const fromInitial = resolveTurnDurationMsFromEngineTimes(
      initialPlayer1TimeLeftMs,
      initialPlayer2TimeLeftMs,
    );
    if (fromInitial != null) {
      return fromInitial;
    }

    return turnDurationMsProp ?? 60_000;
  }, [
    gameUpdate?.player1TimeLeftMs,
    gameUpdate?.player2TimeLeftMs,
    initialPlayer1TimeLeftMs,
    initialPlayer2TimeLeftMs,
    turnDurationMsProp,
  ]);

  const handleCellClick = useCallback(
    (row: number, col: number) => {
      if (isMyTurnRef.current) {
        onMove({ row, col });
      }
    },
    [onMove],
  );

  const handleColumnClick = useCallback(
    (col: number) => {
      if (isMyTurnRef.current) {
        onMove({ col });
      }
    },
    [onMove],
  );

  useEffect(() => {
    if (gameUpdate?.gameState !== undefined) {
      lastGameStateRef.current = gameUpdate.gameState;
    }
    if (gameUpdate?.playerSlot === 0 || gameUpdate?.playerSlot === 1) {
      lastMySlotRef.current = gameUpdate.playerSlot;
    } else if (initialPlayerSlot === 0 || initialPlayerSlot === 1) {
      lastMySlotRef.current = initialPlayerSlot;
    }
  }, [gameUpdate?.gameState, gameUpdate?.playerSlot, initialPlayerSlot]);

  useEffect(() => {
    if (activeLobbyId === null) {
      initializedLobbyIdRef.current = null;
      hasEngineSnapshotRef.current = false;
      setLocalPlayer1Ms(0);
      setLocalPlayer2Ms(0);
      timeUpRequestedRef.current = false;
      chessPlayerSlotRef.current = null;
      battleshipPlayerSlotRef.current = null;
      playerSymbolRef.current = null;
      return;
    }

    if (initializedLobbyIdRef.current === activeLobbyId) {
      return;
    }

    initializedLobbyIdRef.current = activeLobbyId;
    hasEngineSnapshotRef.current = false;
    setLocalPlayer1Ms(0);
    setLocalPlayer2Ms(0);
    timeUpRequestedRef.current = false;
    chessPlayerSlotRef.current = null;
    battleshipPlayerSlotRef.current = null;
    playerSymbolRef.current = null;
  }, [activeLobbyId]);

  useEffect(() => {
    const u = gameUpdate;
    if (
      u &&
      typeof u.player1TimeLeftMs === 'number' &&
      typeof u.player2TimeLeftMs === 'number'
    ) {
      serverEngine0Ref.current = u.player1TimeLeftMs;
      serverEngine1Ref.current = u.player2TimeLeftMs;
      lastUpdateTimeRef.current = Date.now();
      hasEngineSnapshotRef.current = true;
      timeUpRequestedRef.current = false;
      const stateForTick = u.gameState ?? lastGameStateRef.current;
      const mySlot =
        u.playerSlot ?? lastMySlotRef.current ?? initialPlayerSlot ?? null;
      if (stateForTick !== undefined && (mySlot === 0 || mySlot === 1)) {
        const activeSlot = getActiveEngineSlotFromState(gameSlug, stateForTick);
        const { live0, live1 } = applyLiveEngineTimes(
          u.player1TimeLeftMs,
          u.player2TimeLeftMs,
          activeSlot,
          0,
        );
        const mapped = mapEngineSlotTimesToLayout(live0, live1, mySlot);
        setLocalPlayer1Ms(mapped.layoutP1);
        setLocalPlayer2Ms(mapped.layoutP2);
      }
      return;
    }

    if (
      activeLobbyId !== null &&
      typeof initialPlayer1TimeLeftMs === 'number' &&
      typeof initialPlayer2TimeLeftMs === 'number' &&
      !hasEngineSnapshotRef.current
    ) {
      serverEngine0Ref.current = initialPlayer1TimeLeftMs;
      serverEngine1Ref.current = initialPlayer2TimeLeftMs;
      lastUpdateTimeRef.current = Date.now();
      hasEngineSnapshotRef.current = true;
      timeUpRequestedRef.current = false;
      const stateForTick = gameUpdate?.gameState ?? lastGameStateRef.current;
      const mySlot =
        gameUpdate?.playerSlot ??
        lastMySlotRef.current ??
        initialPlayerSlot ??
        null;
      if (stateForTick !== undefined && (mySlot === 0 || mySlot === 1)) {
        const activeSlot = getActiveEngineSlotFromState(gameSlug, stateForTick);
        const { live0, live1 } = applyLiveEngineTimes(
          initialPlayer1TimeLeftMs,
          initialPlayer2TimeLeftMs,
          activeSlot,
          0,
        );
        const mapped = mapEngineSlotTimesToLayout(live0, live1, mySlot);
        setLocalPlayer1Ms(mapped.layoutP1);
        setLocalPlayer2Ms(mapped.layoutP2);
      }
    }
  }, [
    activeLobbyId,
    gameSlug,
    gameUpdate,
    initialPlayer1TimeLeftMs,
    initialPlayer2TimeLeftMs,
    initialPlayerSlot,
  ]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (!hasEngineSnapshotRef.current) {
        return;
      }

      const u = gameUpdateRef.current;
      const stateForTick = u?.gameState ?? lastGameStateRef.current;
      if (stateForTick === undefined) {
        return;
      }

      const mySlot =
        u?.playerSlot ??
        lastMySlotRef.current ??
        initialPlayerSlotRef.current ??
        null;
      if (mySlot !== 0 && mySlot !== 1) {
        return;
      }

      const slug = gameSlugRef.current;
      const activeSlot = getActiveEngineSlotFromState(slug, stateForTick);
      const elapsed = Date.now() - lastUpdateTimeRef.current;
      const s0 = serverEngine0Ref.current;
      const s1 = serverEngine1Ref.current;
      const { live0, live1 } = applyLiveEngineTimes(
        s0,
        s1,
        activeSlot,
        elapsed,
      );
      const { layoutP1, layoutP2 } = mapEngineSlotTimesToLayout(
        live0,
        live1,
        mySlot,
      );
      setLocalPlayer1Ms(layoutP1);
      setLocalPlayer2Ms(layoutP2);

      const myLiveEngine = mySlot === 0 ? live0 : live1;
      const ou = onTimeUpRef.current;
      if (
        activeSlot === mySlot &&
        myLiveEngine === 0 &&
        ou &&
        !timeUpRequestedRef.current
      ) {
        timeUpRequestedRef.current = true;
        ou();
      }
    }, 100);

    return () => window.clearInterval(interval);
  }, []);

  if (gameSlug === 'classic-chess') {
    if (!gameUpdate) {
      return <GameScreenLoading />;
    }
    const rawChessState = gameUpdate.gameState as IChessStateFen | undefined;
    if (!rawChessState?.fen) {
      return <GameScreenLoading />;
    }
    const fen = rawChessState.fen;
    const currentPlayer: TPlayerSlot = fen.split(' ')[1] === 'w' ? 0 : 1;
    if (chessPlayerSlotRef.current === null) {
      chessPlayerSlotRef.current =
        gameUpdate.playerSlot ??
        (gameUpdate.yourTurn ? currentPlayer : ((1 - currentPlayer) as 0 | 1));
    }
    const playerColor: Color = chessPlayerSlotRef.current === 1 ? 'b' : 'w';
    return (
      <GameScreenRoot turnStamp={turnStamp}>
        <ChessGame
          fen={fen}
          isGameOver={false}
          onMove={(from, to, promotion) => {
            onMove({
              from: positionToSquare(from.row, from.col),
              to: positionToSquare(to.row, to.col),
              ...(promotion != null && {
                promotion: promotion.toLowerCase() as 'q' | 'r' | 'b' | 'n',
              }),
            });
          }}
          onSurrender={onSurrender}
          player1={player1}
          player1TimeLeftMs={localPlayer1Ms}
          player2={player2}
          player2TimeLeftMs={localPlayer2Ms}
          playerColor={playerColor}
          rulesContent={rules}
          turnDurationMs={turnDurationMs}
        />
      </GameScreenRoot>
    );
  }

  if (
    gameSlug !== 'battleship' &&
    gameSlug !== 'tic-tac-toe' &&
    gameSlug !== 'four-in-a-row'
  ) {
    return (
      <div className={styles.screen}>
        <div className={styles.centered}>
          <TextBlock className={styles.gameText} size="l">
            Game not found
          </TextBlock>
          <TextBlock className={styles.gameSubtext}>
            The game &quot;{gameSlug}&quot; is not available yet.
          </TextBlock>
        </div>
      </div>
    );
  }

  if (!gameUpdate) {
    return <GameScreenLoading />;
  }

  if (gameSlug === 'battleship') {
    if (!isBattleshipBackendState(gameUpdate.gameState)) {
      return <GameScreenLoading />;
    }
    const backendState = gameUpdate.gameState;
    const currentPlayer = backendState.currentPlayer;

    if (battleshipPlayerSlotRef.current === null) {
      battleshipPlayerSlotRef.current =
        gameUpdate.playerSlot ??
        (gameUpdate.yourTurn ? currentPlayer : ((1 - currentPlayer) as 0 | 1));
    }

    const playerSlot = battleshipPlayerSlotRef.current;
    const isPlayer0AllSunk = checkAllSunk(
      backendState.ships0,
      backendState.shotsAt0,
    );
    const isPlayer1AllSunk = checkAllSunk(
      backendState.ships1,
      backendState.shotsAt1,
    );
    const isGameOver = isPlayer0AllSunk || isPlayer1AllSunk;
    const winner = isPlayer0AllSunk ? 1 : isPlayer1AllSunk ? 0 : null;

    return (
      <GameScreenRoot turnStamp={turnStamp}>
        <BattleshipGame
          isGameOver={isGameOver}
          onShoot={handleCellClick}
          onSurrender={onSurrender}
          player1={player1}
          player1TimeLeftMs={localPlayer1Ms}
          player2={player2}
          player2TimeLeftMs={localPlayer2Ms}
          playerSlot={playerSlot}
          rulesContent={rules}
          state={backendState}
          turnDurationMs={turnDurationMs}
          winner={winner}
        />
      </GameScreenRoot>
    );
  }

  if (gameSlug === 'four-in-a-row') {
    if (!isBackendBoardState(gameUpdate.gameState)) {
      return <GameScreenLoading />;
    }
    const backendState = gameUpdate.gameState as IFourInARowBackendState;
    const board = backendState.board as unknown as (0 | 1 | null)[][];
    const currentPlayer = backendState.currentPlayer;
    const board01 = backendState.board as unknown as TFourInARowBoard01;
    const winCheck01 = checkWinnerFour(board01);
    const isDraw = !winCheck01.hasWinner && checkDrawFour(board01);
    const isGameOver = winCheck01.hasWinner || isDraw;
    const winningLine = winCheck01.winningLine ?? undefined;
    const winner = winCheck01.winner ?? null;

    if (playerSymbolRef.current === null) {
      playerSymbolRef.current = gameUpdate.yourTurn
        ? currentPlayer
        : ((1 - currentPlayer) as 0 | 1);
    }

    const playerSymbol = playerSymbolRef.current as 0 | 1;

    return (
      <GameScreenRoot turnStamp={turnStamp}>
        <FourInARowGame
          board={board}
          currentPlayer={currentPlayer}
          isDraw={isDraw}
          isGameOver={isGameOver}
          isMyTurn={isMyTurn}
          onColumnClick={handleColumnClick}
          onGameEnd={onGameEnd}
          onSurrender={onSurrender}
          player1={player1}
          player1TimeLeftMs={localPlayer1Ms}
          player2={player2}
          player2TimeLeftMs={localPlayer2Ms}
          playerSymbol={playerSymbol}
          rulesContent={rules}
          turnDurationMs={turnDurationMs}
          winner={winner}
          winningLine={winningLine}
        />
      </GameScreenRoot>
    );
  }

  if (!isBackendBoardState(gameUpdate.gameState)) {
    return <GameScreenLoading />;
  }
  const backendState = gameUpdate.gameState as ITicTacToeBackendState;
  const board = convertBackendBoardTicTacToe(backendState.board);
  const currentPlayer = backendState.currentPlayer;
  const tttBoard01 = backendState.board as unknown as TTicTacToeBoard01;
  const winCheck = checkWinnerTtt(tttBoard01);
  const isDraw = !winCheck.hasWinner && checkDrawTtt(tttBoard01);
  const isGameOver = winCheck.hasWinner || isDraw;
  const winningLine = winCheck.winningLine ?? undefined;
  const winner = winCheck.winner ?? null;

  if (playerSymbolRef.current === null) {
    playerSymbolRef.current = gameUpdate.yourTurn
      ? currentPlayer
      : ((1 - currentPlayer) as 0 | 1);
  }

  const playerSymbol = playerSymbolRef.current;

  return (
    <GameScreenRoot turnStamp={turnStamp}>
      <TicTacToeGame
        board={board}
        currentPlayer={currentPlayer}
        isDraw={isDraw}
        isGameOver={isGameOver}
        isMyTurn={isMyTurn}
        onCellClick={handleCellClick}
        onGameEnd={onGameEnd}
        onSurrender={onSurrender}
        player1={player1}
        player1TimeLeftMs={localPlayer1Ms}
        player2={player2}
        player2TimeLeftMs={localPlayer2Ms}
        playerSymbol={playerSymbol}
        rulesContent={rules}
        turnDurationMs={turnDurationMs}
        winner={winner}
        winningLine={winningLine}
      />
    </GameScreenRoot>
  );
};

export interface IGameLoadingBlockProps {
  className?: string;
  'data-testid'?: string;
}

export const GameLoadingBlock: FC<IGameLoadingBlockProps> = ({
  className,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation('matchmaking');

  return (
    <div className={className} data-testid={dataTestId}>
      <div className={styles.centered}>
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
        <TextBlock className={styles.gameText} size="l">
          {t('search.loadingGame')}
        </TextBlock>
      </div>
    </div>
  );
};

export const GameScreenLoading: FC = () => {
  return (
    <GameLoadingBlock
      className={styles.screen}
      data-testid="game-screen-loading"
    />
  );
};
